import {
  fileUpload,
  manufacturerListApi,
  materialGroupListApi,
  materialListApi,
  listOfUomForDropdown,
} from "Api/common";
import {
  createProductApi,
  updateProductApi,
  viewBatchProductApi,
} from "Api/product";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
  checkStatusCodeSuccess,
  deleteUploadedFile,
} from "Components/constants/Common";
import {
  ConditionOptions,
  ProductLabel,
  RakeNoOptions,
  UomValueOptions,
} from "Components/constants/Product";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  digitRegex,
  InputPlaceHolder,
  negDigitRegex,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { FieldArray, Form, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useDropzone } from "react-dropzone";
import { BaseImageURL } from "Api/services";
import onErrorImg from "../../assets/images/onError.jpg";
import Loader from "Components/Base/BaseLoader";

const validationSchema = yup.object({
  productDescription: yup
    .string()
    .required(validationMessages.required(ProductLabel.ProductDescription)),
  materialType: yup
    .string()
    .required(validationMessages.required(ProductLabel.MaterialType)),
  materialGroup: yup
    .string()
    .required(validationMessages.required(ProductLabel.MaterialGroup)),
  uom: yup.string().required(validationMessages.required(ProductLabel.UOM)),
});

const ProductForm = () => {
  const { id, varientId } = useParams();
  document.title = id ? ProductLabel.EditProduct : ProductLabel.AddProduct;
  const [loader, setLoader] = useState<boolean>(false);
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [materialList, setMaterialList] = useState<Array<any>>([]);
  const [materialGroupList, setMaterialGroupList] = useState<Array<any>>([]);
  const [manufacturerList, setManufacturerList] = useState<Array<any>>([]);
  const [uomList, setUomList] = useState<Array<any>>([]);
  const [viewProductData, setViewProductData] = useState<any>([]);
  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<typeof initialValues>>(null);

  const fetchData = () => {
    setLoader(true);
    materialListApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setMaterialList(
            resp?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.name,
              id: item?.id,
            }))
          );
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
    materialGroupListApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setMaterialGroupList(
            resp?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.name,
              id: item?.id,
            }))
          );
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
    manufacturerListApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setManufacturerList(
            resp?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.name,
              id: item?.id,
            }))
          );
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
    listOfUomForDropdown()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setUomList(
            resp?.data?.map((item: any) => ({
              value: item?.id,
              label: item?.uom,
              id: item?.id,
            }))
          );
        } else {
          setUomList(UomValueOptions);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
        setUomList(UomValueOptions);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const fetchProduct = () => {
    setLoader(true);
    viewBatchProductApi(Number(id), Number(varientId))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setViewProductData(resp?.data);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  useEffect(() => {
    if (id) {
      fetchProduct();
    }
    fetchData();
  }, []);

  const initialValues = {
    productCode: id ? viewProductData?.product_code : null,
    productDescription: id ? viewProductData?.product_description : null,
    quantity: id
      ? viewProductData?.productVariant?.[0]?.quantity || null
      : null,
    minimumQuantity: id ? viewProductData?.minimum_quantity || null : null,
    materialType: id ? viewProductData?.material_id || null : null,
    condition: id ? viewProductData?.product_status || null : null,
    materialGroup: id ? viewProductData?.material_group_id || null : null,
    materialGroupDescription: id
      ? viewProductData?.material_group_description || null
      : null,
    manufacturer: id
      ? viewProductData?.productVariant?.[0]?.manufacturer?.id || null
      : null,
    rate: id ? viewProductData?.productVariant?.[0]?.rate || null : null,
    amount: id ? viewProductData?.productVariant?.[0]?.amount || null : null,
    uom: id ? viewProductData?.productUom?.id || null : null,
    rakeNo: id ? viewProductData?.rake_no : null,
    specifications: id ? viewProductData?.specifications : null,
    remark: id ? viewProductData?.remark : null,
    productImage: id ? viewProductData?.productImage || [] : [],
  };

  const onSubmit = (values: any) => {
    setBtnLoader(true);
    const payload = {
      product_description: values.productDescription,
      quantity: Number(values.quantity),
      minimum_quantity: Number(values.minimumQuantity),
      material_id: values.materialType,
      product_status: values.condition ? values.condition : null,
      material_group_id: values.materialGroup,
      material_group_description: values.materialGroupDescription,
      manufacturer_id: values.manufacturer || null,
      rate: Number(values.rate) || null,
      amount: Number(values.amount),
      uom_id: values.uom,
      rake_no: values.rakeNo,
      specifications: values.specifications,
      remark: values.remark,
      productImage: values.productImage.map((img: any) => ({
        product_image: img.product_image,
      })),
    };
    if (id) {
      updateProductApi(Number(id), payload)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message);
            navigate("/product");
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => {
          if (Array.isArray(err?.message)) {
            err?.message?.map((message: string) => {
              toast.error(message);
            });
          } else {
            toast.error(err?.response?.message || err?.message);
          }
          return err;
        })
        .finally(() => {
          setBtnLoader(false);
        });
    } else {
      createProductApi(payload)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message);
            navigate("/product");
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => {
          if (Array.isArray(err?.message)) {
            err?.message?.map((message: string) => {
              toast.error(message);
            });
          } else {
            toast.error(err?.response?.data?.message || err?.message);
          }
          return err;
        })
        .finally(() => {
          setBtnLoader(false);
        });
    }
  };

  const handleFileUpload = (files: File[]) => {
    setLoader(true);
    const payload = new FormData();
    files.forEach((file) => {
      payload.append("files", file);
    });

    fileUpload(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.StatusCodes)) {
          if (formikRef.current) {
            const newImages = resp?.data.map((file: string) => ({
              product_image: file,
            }));
            formikRef.current.setFieldValue("productImage", [
              ...formikRef.current.values.productImage,
              ...newImages,
            ]);
          }
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [] },
    multiple: true,
    onDrop: (acceptedFiles) => {
      handleFileUpload(acceptedFiles);
    },
  });

  const ImageCarousel = ({
    images,
    remove,
  }: {
    images: { product_image: string }[];
    remove: (index: number) => void;
  }) => {
    const [startIndex, setStartIndex] = useState(0);
    const imagesToShow = 4;

    const next = () => {
      if (startIndex + imagesToShow < images.length) {
        setStartIndex(startIndex + 1);
      }
    };

    const previous = () => {
      if (startIndex > 0) {
        setStartIndex(startIndex - 1);
      }
    };

    return (
      <div className="d-flex align-items-center">
        {startIndex > 0 && (
          <div className="mdi mdi-arrow-left" onClick={previous}></div>
        )}

        <div className="d-flex">
          {images
            ?.slice(startIndex, startIndex + imagesToShow)
            ?.map((image, index) => (
              <div key={index} className="card position-relative mx-1">
                <img
                  src={`${BaseImageURL}${image?.product_image}`}
                  alt={`img-${index}`}
                  className="img-fluid img-container"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = onErrorImg;
                  }}
                />
                <i
                  className="position-absolute mdi mdi-close fs-4 text-dark cursor-pointer top-0 end-0"
                  onClick={() => {
                    remove(startIndex + index);
                    handleDelete(image?.product_image);
                  }}
                ></i>
              </div>
            ))}
        </div>

        {startIndex + imagesToShow < images?.length && (
          <i className="mdi mdi-arrow-right" onClick={next}></i>
        )}
      </div>
    );
  };

  const handleDelete = (img: string) => {
    if (img) {
      deleteUploadedFile(img);
    }
  };

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {id ? ProductLabel.EditProduct : ProductLabel.AddProduct}
          </span>
          <BaseButton
            className="btn btn-danger"
            onClick={() => navigate("/product")}
          >
            <i className="mdi mdi-arrow-left" />
          </BaseButton>
        </div>
      </div>
      <div className="card word-wrap">
        <div className="card-body">
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            validateOnChange={true}
            validateOnBlur={true}
            innerRef={formikRef}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit} className="mt-2 mb-4">
                {/* <div className="col-12 fs-5 d-flex justify-content-start align-items-center mb-2">
                  <div className="d-flex flex-column align-items-center justify-content-start">
                    <div
                      className="form-check form-check-success form-switch form-switch-md mx-2"
                      dir="ltr"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input ml-4"
                        id="isReturnable"
                        checked={isReturnable}
                        onChange={() => setIsReturnable(!isReturnable)}
                      />
                      <label
                        className="form-check-label fs-6"
                        htmlFor="isReturnable"
                      >
                        {ProductLabel.ReturnableItem}
                      </label>
                      <i className="mdi mdi-help-circle-outline text-info fs-5 cursor-pointer align-middle tooltip-container">
                        <span className="tooltip-text">
                          {ProductLabel.ReturnableItem}
                        </span>
                      </i>
                    </div>
                  </div>
                </div> */}
                <div className="row">
                  <div className="col-md-6 col-12">
                    <div className="row">
                      <div className="col-md-6 col-sm-6 col-12">
                        <BaseInput
                          type="text"
                          name="productCode"
                          label={ProductLabel.ProductCode}
                          placeholder={InputPlaceHolder(
                            ProductLabel.ProductCode
                          )}
                          value={values.productCode}
                          onChange={handleChange}
                          handleBlur={handleBlur}
                          error={errors.productCode}
                          touched={touched.productCode}
                          disabled
                        />
                      </div>
                      <div className="col-md-6 col-sm-6 col-12">
                        <BaseInput
                          type="textarea"
                          name="productDescription"
                          label={ProductLabel.ProductDescription}
                          placeholder={InputPlaceHolder(
                            ProductLabel.ProductDescription
                          )}
                          value={values.productDescription}
                          onChange={handleChange}
                          handleBlur={handleBlur}
                          error={errors.productDescription}
                          touched={touched.productDescription}
                          required
                        />
                      </div>
                      <div className="col-md-6 col-sm-6 col-12">
                        <BaseInput
                          type="text"
                          name="quantity"
                          label={ProductLabel.Quantity}
                          placeholder={InputPlaceHolder(ProductLabel.Quantity)}
                          value={values.quantity}
                          onChange={(e) => {
                            if (negDigitRegex.test(e.target.value)) {
                              setFieldValue("quantity", e.target.value);
                              setFieldValue(
                                "amount",
                                Number(e.target.value) *
                                  Number(formikRef.current?.values.rate)
                              );
                            }
                          }}
                          handleBlur={handleBlur}
                          error={errors.quantity}
                          touched={touched.quantity}
                        />
                      </div>
                      <div className="col-md-6 col-sm-6 col-12">
                        <BaseInput
                          type="text"
                          name="minimumQuantity"
                          label={ProductLabel.MinimumQuantity}
                          placeholder={InputPlaceHolder(
                            ProductLabel.MinimumQuantity
                          )}
                          value={values.minimumQuantity}
                          onChange={(e) => {
                            if (negDigitRegex.test(e.target.value)) {
                              handleChange(e);
                            }
                          }}
                          handleBlur={handleBlur}
                          error={errors.minimumQuantity}
                          touched={touched.minimumQuantity}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 col-12">
                    <label id="productId" className="form-label">
                      {ProductLabel.Images}
                    </label>
                    <div>
                      <FieldArray name="productImage">
                        {({ remove }) => (
                          <div>
                            <div
                              {...getRootProps()}
                              className="border border-gray-900 custom-dropzone rounded-3 p-4 text-center d-flex align-items-center justify-content-center dz-clickable"
                            >
                              <input {...getInputProps()} />
                              <p>{ProductLabel.DragAndDrop}</p>
                            </div>

                            <div className="row mt-3">
                              {values.productImage?.length <= 4 ? (
                                values.productImage?.map(
                                  (image: any, index: number) => (
                                    <div className="col-3" key={index}>
                                      <div className="card position-relative">
                                        <img
                                          src={`${BaseImageURL}${image?.product_image}`}
                                          alt={`img-${index}`}
                                          className="img-fluid img-container"
                                          onError={(e) => {
                                            const target =
                                              e.target as HTMLImageElement;
                                            target.onerror = null;
                                            target.src = onErrorImg;
                                          }}
                                        />
                                        <i
                                          className="position-absolute mdi mdi-close fs-4 text-dark cursor-pointer top-0 end-0 img-icon"
                                          onClick={() => {
                                            remove(index);
                                            handleDelete(image?.product_image);
                                          }}
                                        ></i>
                                      </div>
                                    </div>
                                  )
                                )
                              ) : (
                                <ImageCarousel
                                  images={values?.productImage}
                                  remove={remove}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                    {touched.productImage && errors.productImage ? (
                      <div className="text-danger">
                        {typeof errors.productImage === "string"
                          ? errors.productImage
                          : null}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="w-full border-bottom my-3"></div>
                <div className="row mt-2">
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseSelect
                      name="materialType"
                      label={ProductLabel.MaterialType}
                      options={materialList}
                      placeholder={SelectPlaceHolder(ProductLabel.MaterialType)}
                      value={values.materialType}
                      handleChange={(field: any, value: any) => {
                        setFieldValue(field, value);
                      }}
                      handleBlur={handleBlur}
                      error={errors.materialType}
                      touched={touched.materialType}
                      required
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseSelect
                      name="condition"
                      label={ProductLabel.Condition}
                      options={ConditionOptions}
                      placeholder={SelectPlaceHolder(ProductLabel.Condition)}
                      value={values.condition}
                      handleChange={(field: any, value: any) => {
                        setFieldValue(field, value);
                      }}
                      handleBlur={handleBlur}
                      error={errors.condition}
                      touched={touched.condition}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseSelect
                      name="materialGroup"
                      label={ProductLabel.MaterialGroup}
                      options={materialGroupList}
                      placeholder={SelectPlaceHolder(
                        ProductLabel.MaterialGroup
                      )}
                      value={values.materialGroup}
                      handleChange={(field: any, value: any) => {
                        setFieldValue(field, value);
                      }}
                      handleBlur={handleBlur}
                      error={errors.materialGroup}
                      touched={touched.materialGroup}
                      required
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="materialGroupDescription"
                      type="textarea"
                      label={ProductLabel.MaterialGroupDescription}
                      placeholder={InputPlaceHolder(
                        ProductLabel.MaterialGroupDescription
                      )}
                      value={values.materialGroupDescription}
                      onChange={handleChange}
                      handleBlur={handleBlur}
                      error={errors.materialGroupDescription}
                      touched={touched.materialGroupDescription}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseSelect
                      name="manufacturer"
                      label={ProductLabel.Make}
                      options={manufacturerList}
                      placeholder={SelectPlaceHolder(ProductLabel.Make)}
                      value={values.manufacturer}
                      handleChange={(field: any, value: any) => {
                        setFieldValue(field, value);
                      }}
                      handleBlur={handleBlur}
                      error={errors.manufacturer}
                      touched={touched.manufacturer}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="rate"
                      type="number"
                      label={ProductLabel.Rate}
                      placeholder={InputPlaceHolder(ProductLabel.Rate)}
                      value={values.rate}
                      onChange={(e) => {
                        setFieldValue("rate", e.target.value);
                        setFieldValue(
                          "amount",
                          Number(e.target.value) *
                            Number(formikRef.current?.values.quantity)
                        );
                      }}
                      handleBlur={handleBlur}
                      error={errors.rate}
                      touched={touched.rate}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="amount"
                      type="number"
                      label={ProductLabel.Amount}
                      placeholder={InputPlaceHolder(ProductLabel.Amount)}
                      value={values.amount}
                      onChange={handleChange}
                      handleBlur={handleBlur}
                      error={errors.amount}
                      touched={touched.amount}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseSelect
                      name="uom"
                      label={ProductLabel.UOM}
                      options={uomList}
                      placeholder={SelectPlaceHolder(ProductLabel.UOM)}
                      value={values.uom}
                      handleChange={(field: any, value: any) => {
                        setFieldValue(field, value);
                      }}
                      handleBlur={handleBlur}
                      error={errors.uom}
                      touched={touched.uom}
                      required
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="rakeNo"
                      label={ProductLabel.RakeNo}
                      placeholder={InputPlaceHolder(ProductLabel.RakeNo)}
                      value={values.rakeNo}
                      onChange={handleChange}
                      handleBlur={handleBlur}
                      error={errors.rakeNo}
                      touched={touched.rakeNo}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="specifications"
                      type="textarea"
                      label={ProductLabel.Specifications}
                      placeholder={InputPlaceHolder(
                        ProductLabel.Specifications
                      )}
                      value={values.specifications}
                      onChange={handleChange}
                      handleBlur={handleBlur}
                      error={errors.specifications}
                      touched={touched.specifications}
                    />
                  </div>
                  <div className="col-md-3 col-sm-6 col-12">
                    <BaseInput
                      name="remark"
                      type="textarea"
                      label={ProductLabel.Remark}
                      placeholder={InputPlaceHolder(ProductLabel.Remark)}
                      value={values.remark}
                      onChange={handleChange}
                      handleBlur={handleBlur}
                      error={errors.remark}
                      touched={touched.remark}
                    />
                  </div>
                </div>

                <div className="row mt-3 d-flex justify-content-end">
                  <div className="col-auto d-flex justify-content-end gap-2 text-end">
                    <BaseButton
                      type="submit"
                      label={id ? updateLabel : submitLabel}
                      color="success"
                      loader={btnLoader}
                      disable={btnLoader}
                    />
                    <BaseButton
                      type="button"
                      label={cancelLabel}
                      onClick={() => navigate("/product")}
                      color="danger"
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
